import classNames from 'classnames'
import { useAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  matchPath,
  NavLink,
  useLocation,
  useRouteMatch,
} from 'react-router-dom'
import { bookIdAtom } from 'store/atoms'
import { DashboardNavContainer } from './style'

import StyledTooltip from 'components/Elements/StyledTooltip'
import { BsPin } from 'react-icons/bs'
import {
  MdArrowForward,
  MdDiversity3,
  MdFactCheck,
  MdMenuBook,
} from 'react-icons/md'
import { useBookQuery, useBookRoleQuery } from 'store/queries'

// TODO: Clean this up once finalized.
function DashboardNav() {
  const { url } = useRouteMatch()
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const [bookId] = useAtom(bookIdAtom)
  const ref = useRef(null)

  const { data: book } = useBookQuery(bookId)
  const { data } = useBookRoleQuery(bookId)
  const role = data?.name
  // TODO: Tidier system for roles.
  const isPurchaser = role === 'Purchaser'
  const isEditor = role === 'Editor'
  const isPurchaserOrEditor = isPurchaser || isEditor
  const canSeeFriends =
    role === 'Purchaser' || role === 'Editor' || role === 'Author'

  const match = matchPath(pathname, {
    path: `${url}/book/:bookId/:section`,
  })
  const activeUrl = match?.params?.section

  const [isExpandedDesktop, setIsExpandedDesktop] = useState(true)
  const [isExpandedMobile, setIsExpandedMobile] = useState(false)
  const [activeSection, setActiveSection] = useState('')
  useEffect(() => {
    if (activeUrl) {
      setActiveSection(activeUrl)
    }
  }, [activeUrl])

  const collapseIfMobClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsExpandedMobile(false)
    }
  }

  useEffect(() => {
    // Bind the event listener
    document.addEventListener('touchstart', collapseIfMobClickOutside)
    document.addEventListener('mousedown', collapseIfMobClickOutside)

    return () => {
      document.removeEventListener('touchstart', collapseIfMobClickOutside)
      document.removeEventListener('mousedown', collapseIfMobClickOutside)
    }
  }, [])

  const onTouchMobileTab = (tab) => {
    if (tab === activeSection) {
      setIsExpandedMobile(false)
      setActiveSection('')
    } else {
      setActiveSection(tab)
      setIsExpandedMobile(true)
    }
  }

  const DashNavLink = ({ to, children, hide = false }) => {
    if (!hide) {
      return (
        <NavLink
          to={to}
          onClick={() => {
            setIsExpandedMobile(false)
          }}
        >
          {children}
        </NavLink>
      )
    }

    return <></>
  }

  return (
    <DashboardNavContainer
      className={classNames({ collapsed: !isExpandedDesktop })}
    >
      <div className="dashboard-nav" ref={ref}>
        <div className="mobile-icons">
          <div
            id="info-icon-mob"
            className={classNames('mobile-icon-wrapper', {
              active: isExpandedMobile && activeSection === 'info',
            })}
          >
            <span
              className="material-symbols-outlined"
              onClick={() => onTouchMobileTab('info')}
            >
              book_3
            </span>
          </div>
          <div
            id="friends-icon-mob"
            className={classNames('mobile-icon-wrapper', {
              active: isExpandedMobile && activeSection === 'friends',
            })}
          >
            <MdDiversity3
              onClick={() => onTouchMobileTab('friends')}
              onBlur={() => onTouchMobileTab('')}
            />
          </div>
          <div
            id="stories-icon-mob"
            className={classNames('mobile-icon-wrapper', {
              active: isExpandedMobile && activeSection === 'stories',
            })}
          >
            <MdMenuBook onClick={() => onTouchMobileTab('stories')} />
          </div>
          <div
            className={classNames('mobile-icon-wrapper', {
              active: isExpandedMobile && activeSection === 'publish',
            })}
          >
            <MdFactCheck onClick={() => onTouchMobileTab('publish')} />
          </div>
        </div>
        <div className="pin-container">
          {isExpandedDesktop ? (
            <BsPin
              className="pin-nav"
              onClick={() => setIsExpandedDesktop(!isExpandedDesktop)}
            />
          ) : (
            <MdArrowForward
              className="pin-nav"
              onClick={() => setIsExpandedDesktop(!isExpandedDesktop)}
            />
          )}
        </div>
        <div
          className={classNames('navbar-section', {
            active: isExpandedMobile && activeSection === 'info',
          })}
        >
          <div className="nav-section-top">
            <div className="navbar-icon">
              <DashNavLink to={`${url}/book/${bookId}/info`}>
                <StyledTooltip
                  title={isExpandedDesktop ? '' : t('book_info')}
                  placement="right"
                >
                  {/* Missing from react-icons */}
                  <span className="material-symbols-outlined">book_3</span>
                </StyledTooltip>
              </DashNavLink>
            </div>
            <DashNavLink to={`${url}/book/${bookId}/info`}>
              <h3 className="span-adjust">{t('book_info')}</h3>
            </DashNavLink>
          </div>
          <div
            className={classNames('navbar-titles', {
              hidden: activeSection !== 'info',
            })}
          >
            <div className="navbar-subsection">
              <DashNavLink to={`${url}/book/${bookId}/info/overview`}>
                <h4>Book</h4>
              </DashNavLink>
              <DashNavLink
                to={`${url}/book/${bookId}/info/setup`}
                hide={!isPurchaser}
              >
                <h4>{t('setup_guide')}</h4>
              </DashNavLink>
              <DashNavLink
                to={`${url}/book/${bookId}/info/settings`}
                hide={!isPurchaserOrEditor}
              >
                <h4>{t('update_book_settings')}</h4>
              </DashNavLink>
              <DashNavLink
                to={`${url}/book/${bookId}/info/preview`}
                hide={!isPurchaserOrEditor}
              >
                <h4>{t('preview_book')}</h4>
              </DashNavLink>
              <DashNavLink to={`${url}/book/${bookId}/info/todo`}>
                <h4>{t('things_to_do')}</h4>
              </DashNavLink>
              <DashNavLink to={`${url}/book/${bookId}/info/activity`}>
                <h4>{t('recent_activity')}</h4>
              </DashNavLink>
            </div>
          </div>
        </div>

        <div
          className={classNames('navbar-section', {
            active: isExpandedMobile && activeSection === 'friends',
            hide: !canSeeFriends,
          })}
        >
          <div className="nav-section-top">
            <div className="navbar-icon">
              <DashNavLink to={`${url}/book/${bookId}/friends/view`}>
                <StyledTooltip
                  title={isExpandedDesktop ? '' : t('friends_and_family')}
                  placement="right"
                >
                  <MdDiversity3 />
                </StyledTooltip>
              </DashNavLink>
            </div>
            <DashNavLink to={`${url}/book/${bookId}/friends`}>
              <h3>{t('friends_and_family')}</h3>
            </DashNavLink>
          </div>
          <div
            className={classNames('navbar-titles', {
              hidden: activeSection !== 'friends',
            })}
          >
            <div className="navbar-subsection">
              <DashNavLink to={`${url}/book/${bookId}/friends/view`}>
                <h4>View All</h4>
              </DashNavLink>
              <DashNavLink to={`${url}/book/${bookId}/friends/add`}>
                <h4>{t('add_people')}</h4>
              </DashNavLink>
              <DashNavLink to={`${url}/book/${bookId}/friends/invites`}>
                <h4>{t('pending_invites')}</h4>
              </DashNavLink>
            </div>
          </div>
        </div>

        <div
          className={classNames('navbar-section', {
            active: isExpandedMobile && activeSection === 'stories',
          })}
        >
          <div className="nav-section-top">
            <div className="navbar-icon">
              <DashNavLink to={`${url}/book/${bookId}/stories`}>
                <StyledTooltip
                  title={isExpandedDesktop ? '' : t('stories')}
                  placement="right"
                >
                  <MdMenuBook />
                </StyledTooltip>
              </DashNavLink>
            </div>
            <DashNavLink to={`${url}/book/${bookId}/stories`}>
              <h3>{t('stories')}</h3>
            </DashNavLink>
          </div>

          <div
            className={classNames('navbar-titles', {
              hidden: activeSection !== 'stories',
            })}
          >
            <div className="navbar-subsection">
              <DashNavLink
                to={`${url}/book/${bookId}/stories/prompt-catalog`}
                hide={!isPurchaserOrEditor}
              >
                <h4 id="prompt-catalog-link">{t('tt_add_new_prompts')}</h4>
              </DashNavLink>
              <DashNavLink
                to={`${url}/book/${bookId}/stories/order`}
                hide={!isPurchaserOrEditor}
              >
                <h4>{t('reorder_pending_prompts')}</h4>
              </DashNavLink>
              <DashNavLink
                to={`${url}/book/${bookId}/stories/frequency`}
                hide={!isPurchaserOrEditor}
              >
                <h4>{t('change_prompt_frequency')}</h4>
              </DashNavLink>
              <DashNavLink
                to={`${url}/book/${bookId}/stories/view/all`}
                hide={!isPurchaserOrEditor}
              >
                <h4>{t('all_prompts')}</h4>
              </DashNavLink>
              <DashNavLink to={`${url}/book/${bookId}/stories/view/mine`}>
                <h4>{t('my_prompts')}</h4>
              </DashNavLink>
              <DashNavLink
                to={`${url}/book/${bookId}/stories/view/sent`}
                hide={!isPurchaserOrEditor}
              >
                <h4>{t('sent_story_prompts')}</h4>
              </DashNavLink>
              <DashNavLink
                to={`${url}/book/${bookId}/stories/view/pending`}
                hide={!isPurchaserOrEditor}
              >
                <h4>{t('pending_story_prompts')}</h4>
              </DashNavLink>
              <DashNavLink to={`${url}/book/${bookId}/stories/view/shared`}>
                <h4>{t('all_shared_stories')}</h4>
              </DashNavLink>
              <DashNavLink
                to={`${url}/book/${bookId}/stories/view/not-shared`}
                hide={!isPurchaserOrEditor}
              >
                <h4>Not Yet Shared</h4>
              </DashNavLink>
            </div>
          </div>
        </div>

        <div
          className={classNames('navbar-section', {
            active: isExpandedMobile && activeSection === 'publish',
            hide: !isPurchaser,
          })}
        >
          <div className="nav-section-top">
            <div className="navbar-icon">
              <DashNavLink to={`${url}/book/${bookId}/publish/overview`}>
                <StyledTooltip
                  title={isExpandedDesktop ? '' : t('complete_book')}
                  placement="right"
                >
                  <MdFactCheck />
                </StyledTooltip>
              </DashNavLink>
            </div>
            <DashNavLink to={`${url}/book/${bookId}/publish`}>
              <h3>{t('complete_book')}</h3>
            </DashNavLink>
          </div>
          <div
            className={classNames('navbar-titles', {
              hidden: activeSection !== 'publish',
            })}
          >
            <div className="navbar-subsection">
              <DashNavLink to={`${url}/book/${bookId}/publish/overview`}>
                <h4>{t('publish_task_list')}</h4>
              </DashNavLink>
              {book.is_publish_started && (
                <>
                  <DashNavLink
                    to={`${url}/book/${bookId}/publish/cover-templates`}
                  >
                    <h4>{t('cover_design')}</h4>
                  </DashNavLink>
                  <DashNavLink to={`${url}/book/${bookId}/publish/preview`}>
                    <h4>{t('preview_book')}</h4>
                  </DashNavLink>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardNavContainer>
  )
}

export default DashboardNav
